import dynamic from 'next/dynamic';
import ComponentWrapper from '../../fragments/component-wrapper/ComponentWrapper.jsx';
import TextScaleWidth from '../../fragments/text-scale-width/TextScaleWidth.jsx';
import CardItem from './CardItem.jsx';
import { COLOURED_SOURCE_POST_TYPES } from './CardCarousel.transform.wp';
import { useLoadCards } from '../../utils/useLoadCards';

import { useRef } from 'react';
import { motion, useTransform } from 'motion/react';

import { useCustomScroll } from '../../utils/useCustomScroll';
import { useIsDesktop } from '../../context/ThemeContextManagement';

import ComponentHeading from '../../fragments/heading/ComponentHeading';

// Swiper is only needed once there are enough cards to scroll. Loading the
// carousel via next/dynamic keeps Swiper out of the bundle for small card sets,
// while still server-rendering its markup on pages that do use it.
const CardCarouselSwiper = dynamic(() => import('./CardCarouselSwiper.jsx'));

export default function CardCarousel({ block = {}, index = 0 }) {
  const { displayCards } = useLoadCards(block);

  const isDesktop = useIsDesktop();

  const isHero = block.cardType === 'hero';

  // 4+ cards can't fit the 3-across desktop row, so they always carousel. A set
  // of 3 or fewer does fit, so it only needs to carousel below 1024 where it
  // would otherwise stack vertically — Swiper's 1024 breakpoint shows all three
  // at once and locks itself, which is the static row. Hero cards are the
  // exception: they're full-bleed feature cards that read fine stacked, so a
  // small hero set stays a grid at every width.
  //
  // The `> 1` guard matters because post-sourced cards arrive client-side via
  // useLoadCards, so displayCards is [] on first render (e.g. the "Related
  // stories" carousel in BlockLayout). One card must not become a one-slide
  // Swiper with a lone pagination bullet.
  const useSwiper =
    displayCards.length > 3 || (displayCards.length > 1 && !isHero);

  // Every slide is visible at desktop, so there's nothing to scroll there.
  const fitsDesktop = displayCards.length <= 3;

  const containerRef = useRef(null);
  const { scrollYProgress } = useCustomScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['-50px', '200px']);

  // Post-sourced carousels (news, events) put their heading inside the grid via
  // ComponentHeading; the hero/custom-card variants use the large offset
  // heading above it.
  const postCarousel = COLOURED_SOURCE_POST_TYPES.includes(
    block.sourcePostType
  );

  // console.log(block);

  return (
    <ComponentWrapper
      block={block}
      className={`CardCarousel ${block.sourcePostType}`}
      ref={containerRef}
    >
      {block.heading && !postCarousel && (
        <div
          className={`l-grid__col ${block?.animateHeading ? 'l-grid__col--m-10-12 l-grid__col--m-push-1-12' : ''}`}
          style={{ position: 'relative' }}
        >
          {block.animateHeading ? (
            <motion.h2
              style={{ y: isDesktop ? y : 0 }}
              className="CardCarousel-heading CardCarousel--animated"
            >
              <TextScaleWidth text={block.heading} />
            </motion.h2>
          ) : (
            <>
              {block.subheading && (
                <p className="CardCarousel-subheading h5">{block.subheading}</p>
              )}
              <h2 className="CardCarousel-heading">{block.heading}</h2>
            </>
          )}
        </div>
      )}

      <div className="l-grid">
        {block.heading && postCarousel && (
          <ComponentHeading heading={block.heading} />
        )}
        <div className="l-grid__col">
          <div
            className={`CardCarousel-containerInner ${fitsDesktop ? 'hide-controls-desktop' : ''}`}
          >
            <div className="CardCarousel-carousel">
              {useSwiper ? (
                <CardCarouselSwiper
                  cards={displayCards}
                  cardType={block.cardType}
                />
              ) : (
                <ul className="CardCarousel-grid">
                  {displayCards.map((card, index) => (
                    <li key={card.databaseId ?? index}>
                      <CardItem
                        card={card}
                        cardType={block.cardType}
                        index={index}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </ComponentWrapper>
  );
}
